/**
 * Every SecurOne call in one file (plan §7). All requests carry the selected
 * customer (customerId) so an actor with several customers can switch.
 * Responses use the { success, data } envelope; the helpers unwrap `data`.
 * Failures carry a machine-readable errorId - see securone.helpers errorMessage.
 */
// Plain axios: the default instance already carries the X-Access-Token header
// and refresh interceptors from utils/axios_base. Importing axios_base here
// would create a store -> api -> axios_base -> store cycle at start-up.
import axios from "axios";

let organisationId = null;

const base = () => `${process.env.VUE_APP_SERVER_URL}/securone`;
const unwrap = (promise) => promise.then((response) => response.data.data);
const clean = (obj) => Object.keys(obj).reduce((acc, k) => (obj[k] === undefined || obj[k] === null || obj[k] === "" ? acc : { ...acc, [k]: obj[k] }), {});
// `extra` may name a customer of its own (the notification watcher follows every
// customer the account reaches). One that is undefined must not wipe the
// selected customer: spread after it, `{ customerId: undefined }` did, so the
// events list and event detail went out with no customer at all. The server
// then took the first customer for a customer or department login - the wrong
// one when there are several - and a Securdia admin, who has no first
// customer, got 404 securone_not_available.
const params = (extra = {}) => ({ params: clean({ ...extra, customerId: extra.customerId || organisationId || undefined }) });
const withOrg = (body = {}) => ({ ...body, customerId: organisationId || undefined });

function multipart(fields, file) {
  const form = new FormData();
  Object.keys(fields).forEach((key) => {
    if (fields[key] === undefined || fields[key] === null) return;
    // arrays travel as JSON; the server parses departmentIds before validating
    form.append(key, Array.isArray(fields[key]) ? JSON.stringify(fields[key]) : fields[key]);
  });
  if (organisationId) form.append("customerId", organisationId);
  if (file) form.append("file", file, file.name);
  return form;
}

export function setOrganisation(id) {
  organisationId = id || null;
}

export function getOrganisation() {
  return organisationId;
}

export default {
  // the gate: does the surface exist for me here, and what may I do
  context: (customerId, unitId) => unwrap(axios.get(`${base()}/context`, { params: clean({ customer_id: customerId || organisationId || undefined, unit_id: unitId || undefined }) })),

  // categories
  categories: ({ departmentId, reachable } = {}) => unwrap(axios.get(`${base()}/categories`, params({ department_id: departmentId, reachable: reachable ? 1 : undefined }))),
  createCategory: (body) => unwrap(axios.post(`${base()}/categories`, withOrg(body))),
  updateCategory: (id, body) => unwrap(axios.put(`${base()}/categories/${id}`, withOrg(body))),
  deleteCategory: (id) => unwrap(axios.delete(`${base()}/categories/${id}`, params())),
  categoryReach: (id) => unwrap(axios.get(`${base()}/categories/${id}/reach`, params())), // T21

  // groups
  groups: ({ departmentId } = {}) => unwrap(axios.get(`${base()}/groups`, params({ department_id: departmentId }))),
  createGroup: (body) => unwrap(axios.post(`${base()}/groups`, withOrg(body))),
  updateGroup: (id, body) => unwrap(axios.put(`${base()}/groups/${id}`, withOrg(body))),
  deleteGroup: (id) => unwrap(axios.delete(`${base()}/groups/${id}`, params())),
  // V7 dry run - what narrowing this group's scope would drop
  scopePreview: (id, { scope, departmentIds }) => unwrap(axios.post(`${base()}/groups/${id}/scope-preview`, withOrg({ scope, departmentIds }))),
  // V2 + V3 - what the member picker may offer (existing group, or a scope for a new one)
  eligibleMembers: (id, { scope, departmentIds } = {}) =>
    id
      ? unwrap(axios.get(`${base()}/groups/${id}/eligible-members`, params()))
      : unwrap(axios.get(`${base()}/groups/eligible-members`, params({ scope, departmentIds: (departmentIds || []).join(",") }))),

  // users and SMS contacts
  users: ({ departmentId } = {}) => unwrap(axios.get(`${base()}/users`, params({ department_id: departmentId }))),
  invite: (body) => unwrap(axios.post(`${base()}/users/invite`, withOrg(body))), // { kind: basic | sms, departmentIds[] }
  importUsers: (body) => unwrap(axios.post(`${base()}/users/import`, withOrg(body))), // { departmentIds: [one], groups[], rows[] } - basic users in bulk
  updateUser: (id, body) => unwrap(axios.put(`${base()}/users/${id}`, withOrg(body))),
  departmentsPreview: (id, departmentIds) => unwrap(axios.post(`${base()}/users/${id}/departments-preview`, withOrg({ departmentIds }))), // V7
  deleteUser: (id) => unwrap(axios.delete(`${base()}/users/${id}`, params())),
  resendInvite: (id) => unwrap(axios.post(`${base()}/users/${id}/resend-invite`, withOrg({}))), // the welcome mail again, new password link (basic users)
  updateSmsContact: (id, body) => unwrap(axios.put(`${base()}/sms-contacts/${id}`, withOrg(body))),
  deleteSmsContact: (id) => unwrap(axios.delete(`${base()}/sms-contacts/${id}`, params())),

  // documents
  documents: ({ departmentId } = {}) => unwrap(axios.get(`${base()}/documents`, params({ department_id: departmentId }))),
  // multipart: the customer also travels as a query param, because the server
  // resolves access before multer has parsed the form fields
  uploadDocument: (fields, file) => unwrap(axios.post(`${base()}/documents`, multipart(fields, file), { headers: { "Content-Type": "multipart/form-data" }, ...params() })),
  addDocumentVersion: (id, file) => unwrap(axios.post(`${base()}/documents/${id}/versions`, multipart({}, file), { headers: { "Content-Type": "multipart/form-data" }, ...params() })),
  updateDocument: (id, body) => unwrap(axios.put(`${base()}/documents/${id}`, withOrg(body))),
  deleteDocument: (id) => unwrap(axios.delete(`${base()}/documents/${id}`, params())),
  documentFile: (id, version = "current", download = false) => unwrap(axios.get(`${base()}/documents/${id}/versions/${version}/file`, params(download ? { download: 1 } : {}))),

  // events - read-only history, plus the voice room (recipients only)
  // `customerId` overrides the selected customer, for the notification watcher:
  // it follows every customer the account reaches, not the one on screen.
  events: (state = "active", { departmentId, customerId } = {}) => unwrap(axios.get(`${base()}/events`, params({ state, department_id: departmentId, customerId }))),
  event: (id, { customerId } = {}) => unwrap(axios.get(`${base()}/events/${id}`, params({ customerId }))),
  // the active broadcasts this account is to be alerted about - recipients only, decided by the server
  notifications: ({ customerId } = {}) => unwrap(axios.get(`${base()}/notifications`, params({ customerId }))),
  voiceToken: (id) => unwrap(axios.get(`${base()}/events/${id}/voice-token`, params())),
  // raise / act on a broadcast (recipients only - the server decides)
  // { categoryId } for a category broadcast, or { title, instruction, resolveCode, groupIds, documentIds } for a free-text alarm
  createEvent: (payload) => unwrap(axios.post(`${base()}/events`, withOrg(typeof payload === "string" ? { categoryId: payload } : payload))),
  acknowledge: (id) => unwrap(axios.post(`${base()}/events/${id}/acknowledge`, withOrg())),
  // text and/or a photo; with a photo the body is multipart (the server parses text from it)
  chat: (id, text, file = null) =>
    file
      ? unwrap(axios.post(`${base()}/events/${id}/chat`, multipart({ text: text || '' }, file), { headers: { "Content-Type": "multipart/form-data" }, ...params() }))
      : unwrap(axios.post(`${base()}/events/${id}/chat`, withOrg({ text }))),
  messageAttachment: (eventId, messageId) => unwrap(axios.get(`${base()}/events/${eventId}/messages/${messageId}/attachment`, params())),
  resolve: (id, accessCode) => unwrap(axios.post(`${base()}/events/${id}/resolve`, withOrg({ accessCode }))),
};
